import { DecisionAction, DecisionActionCompact } from "../../models/home";
import { LanguageService } from "../language/language.service";
import { HomeDecisionActionPresentationRules } from "./home-decision-action-presentation.rules";
import {
  DecisionActionLike,
  DecisionActionPresentation,
  DecisionUrgencyTone,
  DecisionUxSubject
} from "./home-decision-action.types";

export class HomeDecisionLegacyPresentationBuilder {
  constructor(
    private readonly lang: LanguageService,
    private readonly rules: HomeDecisionActionPresentationRules
  ) {}

  buildNoCatalogActionPresentation(
    state: DecisionUxSubject["state"],
    urgency: { label: string; tone: DecisionUrgencyTone },
    surface: "hero" | "detail"
  ): DecisionActionPresentation {
    if (state === "completed") {
      return this.emptyPresentation(urgency, {
        disabledReason: this.lang.translate("home.decisionActionUx.completedReason"),
        summaryLabel: this.lang.translate("home.decisionActionUx.completedTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.completedCopy"),
        statusTone: "complete"
      });
    }
    if (state === "act_without_rush") {
      return this.emptyPresentation(urgency, {
        disabledReason: this.lang.translate("home.decisionActionUx.safeToIgnoreReason"),
        summaryLabel: this.lang.translate("home.decisionActionUx.safeToIgnoreTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.safeToIgnoreCopy")
      });
    }
    return this.emptyPresentation(urgency, {
      disabledReason: this.lang.translate("home.decisionDetail.noActionAvailable"),
      showSecondaryDetail: surface === "hero"
    });
  }

  buildLegacyActionPresentation(
    decision: DecisionUxSubject | null,
    action: DecisionActionCompact | DecisionAction | null
  ): DecisionActionPresentation {
    const urgency = this.rules.buildUrgencyPresentation(decision);
    if (!decision) return this.emptyPresentation(urgency);
    if (decision.state === "completed" || decision.state === "act_without_rush") {
      return this.buildNoCatalogActionPresentation(decision.state, urgency, "detail");
    }
    const missingReason = this.rules.getMissingInformationReason(decision.missingInformation);
    if (missingReason || (decision.missingInformationCount ?? 0) > 0) {
      return this.actionPresentation(action, urgency, {
        primaryLabel: this.lang.translate("home.decisionActionUx.completeMissingInfo"), primaryMode: "inspect",
        disabledReason: missingReason || this.lang.translate("home.decisionActionUx.missingInfoReason"),
        summaryLabel: this.lang.translate("home.decisionActionUx.missingInfoTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.missingInfoCopy"), statusTone: "blocked", showSecondaryDetail: false
      });
    }
    const lowConfidenceReason = this.rules.getLowConfidenceReason(decision.confidence);
    if (lowConfidenceReason) {
      return this.actionPresentation(action, urgency, {
        primaryLabel: this.lang.translate("home.decisionActionUx.reviewEvidence"), primaryMode: "inspect",
        disabledReason: lowConfidenceReason, summaryLabel: this.lang.translate("home.decisionActionUx.lowConfidenceTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.lowConfidenceCopy"), statusTone: "watch", showSecondaryDetail: false
      });
    }
    return this.buildReadyOrBlockedLegacyPresentation(decision, action, urgency);
  }

  buildReadyOrBlockedLegacyPresentation(
    decision: DecisionUxSubject,
    action: DecisionActionCompact | DecisionAction | null,
    urgency: { label: string; tone: DecisionUrgencyTone }
  ): DecisionActionPresentation {
    if (this.rules.hasCatalogDegradation(decision.sourceTrace, action, decision.confidence)) {
      return this.actionPresentation(action, urgency, {
        primaryLabel: this.lang.translate("home.decisionActionUx.reviewRecommendation"), primaryMode: "disabled",
        disabledReason: action?.disabledReason || this.lang.translate("home.decisionActionUx.catalogReason"),
        summaryLabel: this.lang.translate("home.decisionActionUx.catalogTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.catalogCopy"), statusTone: "watch"
      });
    }
    if (decision.state === "blocked") {
      return this.actionPresentation(action, urgency, {
        primaryLabel: this.lang.translate("home.decisionActionUx.resolveBlock"), primaryMode: "inspect",
        disabledReason: action?.disabledReason || this.lang.translate("home.decisionActionUx.blockedReason"),
        summaryLabel: this.lang.translate("home.decisionActionUx.blockedTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.blockedCopy"), statusTone: "blocked", showSecondaryDetail: false
      });
    }
    if (decision.state === "waiting") {
      return this.actionPresentation(action, urgency, {
        primaryLabel: this.lang.translate("home.decisionActionUx.waitingLabel"), primaryMode: "disabled",
        disabledReason: action?.disabledReason || this.lang.translate("home.decisionActionUx.waitingReason"),
        summaryLabel: this.lang.translate("home.decisionActionUx.waitingTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.waitingCopy"), statusTone: "watch"
      });
    }
    if (this.rules.hasPayloadSchema(action)) {
      return this.actionPresentation(action, urgency, {
        primaryLabel: this.lang.translate("home.decisionActionUx.confirmRequiredData"), primaryMode: "inspect",
        disabledReason: this.lang.translate("home.decisionDetail.actionNeedsForm"),
        summaryLabel: this.lang.translate("home.decisionActionUx.disabledTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.disabledCopy"), statusTone: "watch", showSecondaryDetail: false
      });
    }
    if (!action) return this.emptyPresentation(urgency, { disabledReason: this.lang.translate("home.decisionDetail.noActionAvailable") });
    if (!action.enabled) {
      return this.actionPresentation(action, urgency, {
        primaryLabel: this.lang.translate("home.decisionActionUx.reviewRequirements"), primaryMode: "disabled",
        disabledReason: action.disabledReason || this.lang.translate("home.decisionDetail.actionDisabled"),
        summaryLabel: this.lang.translate("home.decisionActionUx.disabledTitle"),
        summaryCopy: this.lang.translate("home.decisionActionUx.disabledCopy"), statusTone: "watch"
      });
    }
    return this.actionPresentation(action, urgency, {
      primaryLabel: action.label || this.lang.translate("home.decisionDetail.noActionAvailable"), primaryMode: "execute", canExecute: true,
      summaryLabel: this.lang.translate("home.decisionActionUx.readyTitle"),
      summaryCopy: this.rules.getActionDescription(action) || this.lang.translate("home.decisionActionUx.readyCopy"), statusTone: "ready"
    });
  }

  actionPresentation(
    action: DecisionActionLike | null,
    urgency: { label: string; tone: DecisionUrgencyTone },
    overrides: Partial<DecisionActionPresentation>
  ): DecisionActionPresentation {
    return {
      action, primaryLabel: null, primaryMode: "none", canExecute: false, disabledReason: null,
      summaryLabel: this.lang.translate("home.decisionActionUx.noActionTitle"),
      summaryCopy: this.lang.translate("home.decisionActionUx.noActionCopy"), statusTone: "neutral",
      urgencyLabel: urgency.label, urgencyTone: urgency.tone, showSecondaryDetail: true, ...overrides
    };
  }

  emptyPresentation(
    urgency: { label: string; tone: DecisionUrgencyTone },
    overrides: Partial<DecisionActionPresentation> = {}
  ): DecisionActionPresentation {
    return this.actionPresentation(null, urgency, overrides);
  }
}
